import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { postCourseStage } from '~/backend/connector-next-api/courseStageClient';
import type { CourseStage } from '../model';

export type CourseStageChange = {
    stage: CourseStage;
    done: readonly CourseStage[];
};

export type SetCourseStage = {
    setCourseStage: (change: CourseStageChange) => void;
    isSaving: boolean;
    /** True after a failed write, until the next attempt. */
    isError: boolean;
};

/**
 * Writes a patient's course stage and the stages marked done, guarded by the
 * patient's current version (If-Match), and refreshes what the worklist shows.
 *
 * Both the list rows and the patient detail carry the stage, so both are
 * invalidated: leaving the list stale would show the old stage next to a
 * panel showing the new one. The shared `['api', 'patients']` prefix covers
 * the detail query and every page of the list walk.
 */
export function useSetCourseStage(
    patientId: string,
    version: string | undefined
): SetCourseStage {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: ({ stage, done }: CourseStageChange) => {
            if (version === undefined) {
                // Nothing to guard the write with, so it is not attempted: an
                // unconditional POST would overwrite a stage someone else set
                // while this panel was loading.
                return Promise.reject(
                    new Error('The patient version is not known yet.')
                );
            }
            return postCourseStage(patientId, stage, [...done], version);
        },
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: ['api', 'patients'] }),
    });

    const { mutate } = mutation;
    const setCourseStage = useCallback(
        (change: CourseStageChange) => mutate(change),
        [mutate]
    );

    return {
        setCourseStage,
        isSaving: mutation.isPending,
        isError: mutation.isError,
    };
}
